package com.siriusxm.pia

import androidx.compose.runtime.*
import com.siriusxm.cmc.ApplicationStyles
import com.siriusxm.cmc.ApplicationView
import com.siriusxm.cmc.updateDarkMode
import com.siriusxm.cmc.utils.Route
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.HomeStyles
import com.siriusxm.pia.views.account.AccountStyles
import com.siriusxm.pia.views.account.PopupStyles
import com.siriusxm.pia.views.account.UserAccountPopup
import com.siriusxm.pia.views.applicationsList
import com.siriusxm.pia.views.unifiedaggregator.AggregatorStyles
import com.siriusxm.pia.views.unifiedaggregator.smithy.SmithyViewerStyles
import com.siriusxm.pia.views.unifiedaggregator.styles.AggregatorAnimation
import com.siriusxm.podcasts.cmc.EpisodeStyles
import com.siriusxm.unifiedcontent.console.subscriber.SubscriberConnectionDialog
import com.siriusxm.unifiedcontent.console.subscriber.SubscriberConnection
import com.siriusxm.unifiedcontent.console.player.AudioPlayerView
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.dom.*

object HeaderStyles : StyleSheet() {
    val header by style {
        height(40.px)
        flexShrink(0)
        backgroundImage("url(/images/Blue_Purple_All.jpg)")
        color(Color.white)

        display(DisplayStyle.Flex)

        position(Position.Relative)
        boxShadow("0px 3px 6px rgba(0,0,0,.4)")
        property("z-index", "1")

        child(self, type("a")).style {
            whenNarrow {
                flexGrow(1)
            }
        }
    }

    val mainTitle by style {
        lineHeight(40.px)
        fontWeight("bold")
        flexGrow(1)

        display(DisplayStyle.None)
        whenWiderThanPhone {
            display(DisplayStyle.Block)
        }
    }

    val servicesButton by style {
        lineHeight(40.px)
        width(40.px)
        flexGrow(0)
        cursor("pointer")
    }

    val accountActions by style {
        lineHeight(40.px)
        textAlign("right")
        paddingRight(10.px)
        flexGrow(1)

        fontSize((0.8).cssRem)

        display(DisplayStyle.None)
        type("a").style {
            color(Color.white)

            self + hover style {
                color(SXMUI.highlightColor.value())
                textDecoration("none")
            }
        }

        whenWiderThanPhone {
            display(DisplayStyle.Block)
        }
    }

    val servicesPopup by style {
        position(Position.Absolute)
        top(40.px)
        left(225.px)
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        shadowBorder()

        whenNarrow {
            right(0.px)
            left(auto)
        }
    }

    val accountButton by style {
        cursor("pointer")
        marginRight(10.px)
        property("display", "inline-flex")
        alignItems(AlignItems.Center)
        property("vertical-align", "middle")
        color(Color.white)
        lineHeight(40.px)
    }

    val servicesAccountAction by style {
        padding(1.cssRem)
        whenWiderThanPhone {
            display(DisplayStyle.None)
        }
    }

    /**
     * Wrapper for the global timezone selector in the header. Renders the picker inline-aligned
     * with the other account actions (dark-mode toggle, account button).
     *
     * The shared picker's collapsed trigger is styled as a light bordered "chip" for use above
     * light list views; that reads as a jarring solid white box against the dark header. So here we
     * override the trigger (only when nested under this wrapper) to be transparent with white text
     * and no border, matching the minimal icon controls beside it. The open popup keeps its own
     * surface styling since it renders as an overlay.
     */
    val headerTimeZone by style {
        property("display", "inline-flex")
        alignItems(AlignItems.Center)
        property("vertical-align", "middle")
        marginRight(10.px)

        desc(self, className(TimeZoneStyles.tzPickerTrigger)) style {
            backgroundColor(Color.transparent)
            color(Color.white)
            property("border", "none")
            padding(2.px, 4.px)

            self + hover style {
                color(SXMUI.highlightColor.value())
            }
        }
    }
}

/**
 * The root view of the entire application
 */
@Composable
fun renderPage(
    app: Application, route: Route,
    notifications: NotificationCenter,
    router: @Composable (Route) -> Unit
) {
    // Install the global styles. This is a topic of improvement for Compose Web.
    ApplicationStyles()
    Style(HeaderStyles)
    Style(HomeStyles)
    Style(AggregatorStyles)
    Style(AggregatorAnimation)
    Style(EpisodeStyles)
    Style(SmithyViewerStyles)
    Style(AccountStyles)
    Style(PopupStyles)
    Style(TimeZoneStyles)

    Notifications(notifications)
//    Application.notifications = notifications()

    // Initialize the portal system for popups, tooltips, and overlays
    PortalContainer()

    var showingServicesPopup by remember { mutableStateOf(false) }

    // will remain null unless it's changed during execution
    var darkMode by remember { mutableStateOf<Boolean?>(null) }

    LaunchedEffect(route.fullPath) {
        showingServicesPopup = false
    }

    // The page is a full-viewport column: header, application content, then the audio player when
    // something is playing. The content takes whatever height the other two leave, so it (and every
    // scroll container inside it, including embedded apps' iframes) ends where the player begins.
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            height(100.vh)
        }
        onClick {
            showingServicesPopup = false
        }
    }) {
        Div({
            classes(HeaderStyles.header)
        }) {
            A(href = "#") {
                Img("images/SiriusXM-Logo-onDark.svg", attrs = {
                    style {
                        height(100.percent)
                        display(DisplayStyle.InlineBlock)
                        property("vertical-align", "middle")
                        marginRight(20.px)
                        opacity(50.percent)
                    }
                })
            }

            Div({ classes(HeaderStyles.servicesButton) }) {
                icon("apps") {
                    action { showingServicesPopup = !showingServicesPopup }
                }
            }

            Div({ classes(HeaderStyles.mainTitle) }) {
                Text("Unified Content Management")
            }

            Div({ classes(HeaderStyles.accountActions) }) {
                Span({ classes(HeaderStyles.headerTimeZone) }) {
                    HeaderTimeZoneSelector()
                }

                Span {
                    icon("dark_mode") {
                        if (darkMode != true) {
                            this.type = IconType.SYMBOLS
                        }
                        this.action {
                            updateDarkMode(!(darkMode ?: false))
                        }
                    }
                }

                app.viewer.email?.let {
                    var accountHoverIntent by remember { mutableStateOf(false) }
                    var accountShowPopup by remember { mutableStateOf(false) }
                    var accountElement by remember { mutableStateOf<org.w3c.dom.Element?>(null) }

                    // Delay popup dismiss so the mouse can cross the gap between
                    // the trigger and the portal without the popup closing.
                    LaunchedEffect(accountHoverIntent) {
                        if (accountHoverIntent) {
                            accountShowPopup = true
                        } else {
                            delay(150)
                            if (!accountHoverIntent) {
                                accountShowPopup = false
                            }
                        }
                    }

                    Span({
                        classes(HeaderStyles.accountButton)
                        ref { element ->
                            accountElement = element
                            onDispose { accountElement = null }
                        }
                        onMouseEnter { accountHoverIntent = true }
                        onMouseLeave { accountHoverIntent = false }
                        onClick {
                            app.navigate("account")
                        }
                    }) {
                        svgIcon(SVGIcons.user) {
                            size = 24.px
                        }
                    }

                    PositionedPortal(
                        isOpen = accountShowPopup,
                        onDismiss = { accountHoverIntent = false },
                        triggerElement = accountElement,
                        popupAlign = PopupAlign.Right
                    ) {
                        Div({
                            onMouseEnter { accountHoverIntent = true }
                            onMouseLeave { accountHoverIntent = false }
                        }) {
                            UserAccountPopup { accountHoverIntent = false }
                        }
                    }
                }
            }
        }

        ApplicationView(fillParent = true) {
            LaunchedEffect(it) {
                darkMode = it.theme == "dark"
            }
            router(route)
        }

        // Renders nothing when no track is loaded, so the content gets the full height back.
        // Positioned so its upward shadow paints over the content above it.
        Div({
            style {
                flexShrink(0)
                position(Position.Relative)
                property("z-index", "1")
            }
        }) {
            AudioPlayerView(app.audioPlayer)
            SubscriberConnectionDialog()
        }
    }

    if (showingServicesPopup) {
        Div({
            classes(HeaderStyles.servicesPopup)
        }) {
            applicationsList(app, app.configuration.applications.orEmpty().filter { it.internal() })

            Div({
                classes(HeaderStyles.servicesAccountAction)
            }) {
                A(href = "#", {
                    onClick {
                        it.preventDefault()
                        SubscriberConnection.logout()
                    }
                }) {
                    Text("Sign Out")
                }
            }
        }
    }
}
